/**
 * Transaction Table Component
 *
 * Table displaying transactions with sortable columns,
 * header checkbox for bulk selection, and scroll support.
 *
 * @fileoverview Transaction data table component
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Checkbox,
  IconButton,
  HStack,
  Text,
  Alert,
  AlertIcon,
  Button,
  Icon,
} from '@chakra-ui/react';
import { MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { PlaidTransaction, TransactionSort, TransactionSortColumn } from '../../types/transactions';
import TransactionRow from './TransactionRow';

/**
 * Column definition
 */
interface ColumnDef {
  key: string;
  label: string;
  sortKey?: TransactionSortColumn;
  width: string;
}

/**
 * Table columns configuration
 */
const columns: ColumnDef[] = [
  { key: 'checkbox', label: '', width: '40px' },
  { key: 'date', label: 'Date', sortKey: 'date', width: '100px' },
  { key: 'merchant', label: 'Merchant', sortKey: 'merchantName', width: '250px' },
  { key: 'normalizedMerchant', label: 'Vendor', width: '180px' },
  { key: 'amount', label: 'Amount', sortKey: 'amount', width: '120px' },
  { key: 'flow', label: 'Flow', width: '90px' },
  { key: 'category', label: 'Category', sortKey: 'personalFinanceCategoryPrimary', width: '200px' },
  { key: 'payment', label: 'Payment', sortKey: 'paymentChannel', width: '100px' },
  { key: 'account', label: 'Account', sortKey: 'name', width: '150px' },
  { key: 'actions', label: '', width: '50px' },
];

/**
 * Props for TransactionTable component
 */
interface CategoryOption {
  primary: string;
  detailed: string;
}

interface TransactionTableProps {
  transactions: PlaidTransaction[];
  selectedIds: Set<string>;
  selectAllMode: 'none' | 'displayed' | 'all';
  totalCount: number;
  sort: TransactionSort;
  onToggleSort: (column: TransactionSortColumn) => void;
  onSelectTransaction: (id: string) => void;
  onSelectAllDisplayed: () => void;
  onSelectAll: () => void;
  onEdit?: (transaction: PlaidTransaction) => void;
  onInlineSave?: (id: string, updates: { merchant?: string; pfc_primary?: string; pfc_detailed?: string }) => Promise<void>;
  onSaveVendor?: (id: string, vendor: string) => Promise<void>;
  onRevert?: (transaction: PlaidTransaction) => void;
  isEditable?: boolean;
  availableCategories?: CategoryOption[];
  recentlyEditedId?: string | null;
  /**
   * Suppresses the "Select all {totalCount} transactions" banner/action.
   * Set this when the caller's onSelectAll can only ever select what's
   * currently loaded (e.g. one server-paginated page) rather than every row
   * matching the active filters — showing that banner there would promise a
   * scope the caller can't actually deliver. Defaults to false everywhere
   * the caller genuinely has (or accumulates) every row, e.g. Home's
   * "Load More" list.
   */
  disableCrossPageSelectAll?: boolean;
  /** Forwarded to every row — see TransactionRow's prop of the same name. */
  onEditingActiveChange?: (commit: (() => Promise<boolean>) | null) => void;
  /**
   * When true, this component becomes its own vertical scroll container
   * (filling whatever height a flex-column ancestor gives it) instead of
   * growing to its natural height and relying on the page/document to
   * scroll. This is what makes the sticky `<Thead>` below actually stick:
   * `position: sticky` anchors to the nearest *scrolling* ancestor, and
   * when the page itself scrolls (the default, `containedScroll={false}`),
   * that ancestor never scrolls internally — so the header scrolls away
   * with everything else instead of staying pinned (see
   * docs/plans/UI_LAYOUT_DENSITY_PLAN.md §H4).
   *
   * Default false preserves today's exact behavior for callers that still
   * rely on page/document scroll (Home's `TransactionIndex`,
   * `PaymentsReport`). Only `TransactionsExplorer`'s dense table-page
   * callers (`/transactions`, `/snapshots`) opt in.
   */
  containedScroll?: boolean;
  /** Adds a sortable "Check #" column after Date (PaymentsReport's Checks tab). */
  showCheckNumber?: boolean;
}

/**
 * Transaction Table Component
 *
 * Displays transactions in a scrollable table with:
 * - Sortable column headers
 * - Header checkbox for bulk selection
 * - "Select all X transactions" banner
 * - Horizontal and vertical scrolling
 */
const TransactionTable: React.FC<TransactionTableProps> = ({
  transactions,
  selectedIds,
  selectAllMode,
  totalCount,
  sort,
  onToggleSort,
  onSelectTransaction,
  onSelectAllDisplayed,
  onSelectAll,
  onEdit,
  onInlineSave,
  onSaveVendor,
  onRevert,
  isEditable = false,
  availableCategories,
  recentlyEditedId,
  disableCrossPageSelectAll = false,
  onEditingActiveChange,
  containedScroll = false,
  showCheckNumber = false,
}) => {
  // Determine header checkbox state
  const allDisplayedSelected =
    transactions.length > 0 && transactions.every((t) => selectedIds.has(t.id));
  const someSelected = selectedIds.size > 0 && !allDisplayedSelected;

  // Dynamic columns based on isEditable
  // We keep 'actions' always for the View button, but might change label or width if needed.
  // User asked to "Add extra column... called 'Actions'".
  // existing columns has { key: 'actions', label: '', width: '50px' }.
  // Let's update the label if isEditable.
  const tableColumns = React.useMemo(() => {
     return columns.flatMap(col => {
         if (col.key === 'actions' && isEditable) {
             return [{ ...col, label: 'Actions' }];
         }
         if (col.key === 'date' && showCheckNumber) {
             return [col, { key: 'checkNumber', label: 'Check #', sortKey: 'checkNumber' as const, width: '90px' }];
         }
         return [col];
     });
  }, [isEditable, showCheckNumber]);

  /**
   * Render sort indicator
   */
  const renderSortIndicator = (column: TransactionSortColumn) => {
    if (sort.column !== column) return null;
    return sort.direction === 'asc' ? (
      <Icon as={MdArrowUpward} boxSize={3} />
    ) : (
      <Icon as={MdArrowDownward} boxSize={3} />
    );
  };

  /**
   * Handle header checkbox change
   */
  const handleHeaderCheckboxChange = () => {
    onSelectAllDisplayed();
  };

  return (
    <Box
      display={containedScroll ? 'flex' : undefined}
      flexDirection={containedScroll ? 'column' : undefined}
      // flex:1 (not height:100%) so this doesn't depend on every ancestor
      // resolving a percentage height correctly — it just takes whatever
      // definite space its flex-column parent already computed, same
      // pattern as every other link in this chain (see
      // TransactionsExplorer's containedScroll wrappers).
      flex={containedScroll ? '1' : undefined}
      minH={containedScroll ? 0 : undefined}
    >
      {/* Select all banner */}
      {!disableCrossPageSelectAll && selectAllMode === 'displayed' && allDisplayedSelected && totalCount > transactions.length && (
        <Alert status="info" bg="bg.muted" borderRadius="md" mb={2} py={2} flexShrink={0}>
          <AlertIcon color="accent.primary" />
          <Text fontSize="sm" color="text.secondary">
            All {transactions.length} transactions on this page are selected.
          </Text>
          <Button
            size="xs"
            variant="link"
            color="accent.primary"
            ml={2}
            onClick={onSelectAll}
          >
            Select all {totalCount} transactions
          </Button>
        </Alert>
      )}

      {selectAllMode === 'all' && (
        <Alert status="success" bg="bg.muted" borderRadius="md" mb={2} py={2} flexShrink={0}>
          <AlertIcon color="green.400" />
          <Text fontSize="sm" color="text.secondary">
            All {totalCount} transactions are selected.
          </Text>
        </Alert>
      )}

      {/*
        Table container. In the default (page-scrolls) mode this expands
        naturally and the page handles vertical scroll — unchanged from
        before. In `containedScroll` mode it's `flex=1 minH=0` so it fills
        whatever height the caller's flex column gives it and becomes the
        scrollport the sticky `<Thead>` below anchors to (see the prop's
        doc comment for why that's required, not optional).
      */}
      <Box
        flex={containedScroll ? 1 : undefined}
        minH={containedScroll ? 0 : undefined}
        overflowX="auto"
        overflowY={containedScroll ? 'auto' : undefined}
        borderRadius="md"
        borderWidth="1px"
        borderColor="xacto.borderGray"
        bg="bg.primary"
      >
        <Table variant="unstyled" size="sm" minW={showCheckNumber ? '1100px' : '1010px'} sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead position="sticky" top={0} zIndex={1}>
            <Tr>
              {tableColumns.map((col) => (
                <Th
                  key={col.key}
                  w={col.width}
                  minW={col.width}
                  px={2}
                  py={2}
                  bg="bg.surface"
                  color="text.secondary"
                  fontWeight="semibold"
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  borderBottomWidth="1px"
                  borderColor="border.default"
                  isNumeric={col.key === 'amount'}
                  // variant="unstyled" ignores isNumeric's alignment, so set it explicitly.
                  textAlign={col.key === 'checkNumber' ? 'right' : undefined}
                >
                  {col.key === 'checkbox' ? (
                    <Checkbox
                      isChecked={allDisplayedSelected && transactions.length > 0}
                      isIndeterminate={someSelected}
                      onChange={handleHeaderCheckboxChange}
                      colorScheme="cyan"
                      borderColor="xacto.borderGray"
                    />
                  ) : col.sortKey ? (
                    <HStack
                      spacing={1}
                      justify={col.key === 'checkNumber' ? 'flex-end' : undefined}
                      cursor="pointer"
                      onClick={() => onToggleSort(col.sortKey!)}
                      _hover={{ color: 'xacto.electricCyan' }}
                    >
                      <Text>{col.label}</Text>
                      {renderSortIndicator(col.sortKey)}
                    </HStack>
                  ) : (
                    col.label
                  )}
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {transactions.map((transaction) => (
              <TransactionRow
                key={transaction.id}
                transaction={transaction}
                normalizedMerchant={
                  transaction.normalizedMerchant || ''
                }
                isSelected={
                  selectAllMode === 'all' || selectedIds.has(transaction.id)
                }
                onSelect={onSelectTransaction}
                onEdit={isEditable ? onEdit : undefined}
                onInlineSave={isEditable ? onInlineSave : undefined}
                onSaveVendor={onSaveVendor}
                onRevert={isEditable ? onRevert : undefined}
                availableCategories={availableCategories}
                isRecentlyEdited={recentlyEditedId === transaction.id}
                onEditingActiveChange={onEditingActiveChange}
                showCheckNumber={showCheckNumber}
              />
            ))}
          </Tbody>
        </Table>

        {/* Empty state */}
        {transactions.length === 0 && (
          <Box py={8} textAlign="center">
            <Text color="text.muted">No transactions found</Text>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TransactionTable;
